// My Tasks on the QEW Dashboard. Pure: tasks and authorizations in, card out.
// Words: QEW_DASHBOARD_WORDS.md, Tasks section (approved 2026-10-06).
// Mockup: MOCKUP_QEW_DASHBOARD_APPROVED.html screens 1 and 7.
//
// Rules:
// * Only tasks switched on. Grouped Tier 1, 2, 3. Collapsible, all closed by default.
// * Read only. A task is authorized at the equipment, never here.
// * Task records start clean. Every task opens Never authorized.
// * Currency is the Task Authorization Cycle (taskAuthCycleDays, 1,095 days,
//   capped at 1,095 per NFPA 70E 110.2(B)(3)). The last 30 days are amber.
//   Same windows as the live app (QEW.gs, Session 72 lock).
// * The newest of the last SOP review and the last time performed counts.

import { daysBetween } from "./qew-training";

export const TASK_AUTH_CYCLE_CAP_DAYS = 1095;
export const TASK_LAPSING_DAYS = 30;

/** A task as copied to tasks/{taskId}. */
export interface TaskDoc {
  taskId: string;
  name: string;
  tier: number;
  isActive: boolean;
  sopNumber?: string | null;
  /** Observe, Operate-close, Expose. The JSP's Field Assessment and verdict read it. */
  taskType?: string;
}

/** One QEW's standing on one task. Written at the equipment (Stage 4). */
export interface TaskAuthorization {
  taskId: string;
  lastSopReviewOn: string | null;
  lastPerformedOn: string | null;
}

export type TaskState = "current" | "lapsing" | "lapsed" | "never";

export interface TaskRow {
  taskId: string;
  name: string;
  state: TaskState;
  line: string;
  tag: string;
}

export interface TierList {
  tier: 1 | 2 | 3;
  title: string;
  sub: string;
  rows: TaskRow[];
  currentCount: number;
  tone: "green" | "amber" | "grey";
}

export interface MyTasksCard {
  tiers: TierList[];
  currentCount: number;
  total: number;
}

export const TIER_SUB: Record<1 | 2 | 3, string> = { 1: "Observation", 2: "Energized diagnostic", 3: "ESWC required" };

function us(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

/** The cycle from Program Settings, held to the 3-year cap. */
export function cycleDays(setting: unknown): number {
  const n = typeof setting === "number" && Number.isFinite(setting) && setting > 0 ? Math.floor(setting) : TASK_AUTH_CYCLE_CAP_DAYS;
  return Math.min(n, TASK_AUTH_CYCLE_CAP_DAYS);
}

/** One task's row for one QEW. */
export function taskRowOf(task: TaskDoc, auth: TaskAuthorization | undefined, cycle: number, today: string): TaskRow {
  const base = { taskId: task.taskId, name: task.name };
  const reviewed = auth?.lastSopReviewOn ?? null;
  const performed = auth?.lastPerformedOn ?? null;
  const last = [reviewed, performed].filter((d): d is string => Boolean(d)).sort().pop() ?? null;
  if (!last) return { ...base, state: "never", line: "Never authorized", tag: "Not yet" };
  const lineOf = last === performed && performed !== reviewed ? `Performed ${us(last)}` : `SOP reviewed ${us(last)}`;
  const age = daysBetween(last, today);
  if (age <= cycle - TASK_LAPSING_DAYS) return { ...base, state: "current", line: lineOf, tag: "Current" };
  if (age <= cycle) return { ...base, state: "lapsing", line: lineOf, tag: "Lapsing" };
  return { ...base, state: "lapsed", line: `Lapsed · ${lineOf}`, tag: "Lapsed" };
}

export function myTasksCard(tasks: readonly TaskDoc[], auths: readonly TaskAuthorization[], cycleSetting: unknown, today: string): MyTasksCard {
  const cycle = cycleDays(cycleSetting);
  const byTask = new Map(auths.map((a) => [a.taskId, a]));
  const on = tasks.filter((t) => t.isActive && (t.tier === 1 || t.tier === 2 || t.tier === 3));
  const tiers = ([1, 2, 3] as const).map((tier): TierList => {
    const rows = on
      .filter((t) => t.tier === tier)
      .sort((a, b) => a.taskId.localeCompare(b.taskId))
      .map((t) => taskRowOf(t, byTask.get(t.taskId), cycle, today));
    // Mockup screen 7: one Current and one Lapsing read "1 of 11".
    const currentCount = rows.filter((r) => r.state === "current").length;
    const tone = rows.some((r) => r.state === "lapsing") ? "amber" : rows.length > 0 && currentCount === rows.length ? "green" : "grey";
    return { tier, title: `Tier ${tier}`, sub: TIER_SUB[tier], rows, currentCount, tone };
  });
  return {
    tiers,
    currentCount: tiers.reduce((n, t) => n + t.currentCount, 0),
    total: tiers.reduce((n, t) => n + t.rows.length, 0),
  };
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const GLYPH: Record<TaskState, string> = { current: "✓", lapsing: "!", lapsed: "–", never: "–" };

/**
 * The card as HTML. open holds the tiers the QEW has opened. Each tier row is a
 * button with data-fa="tier" and data-tier, so the page owns the toggle.
 */
export function myTasksCardHtml(card: MyTasksCard, open: ReadonlySet<number>): string {
  const tiers = card.tiers
    .map((t) => {
      const isOpen = open.has(t.tier);
      const head = `<button class="mt-fold" type="button" data-fa="tier" data-tier="${t.tier}" aria-expanded="${isOpen}">
        <span class="chip ${t.tone}"></span><span class="t"><b>${t.title}</b><small>${esc(t.sub)}</small></span>
        <span class="f">${t.currentCount} of ${t.rows.length}</span><span class="ar">${isOpen ? "▾" : "›"}</span></button>`;
      if (!isOpen) return head;
      const rows = t.rows
        .map(
          (r) => `<div class="mt-row"><span class="gl ${r.state}">${GLYPH[r.state]}</span><span class="t"><b>${esc(r.name)}</b><small>${esc(r.line)}</small></span><span class="tg ${r.state}">${esc(r.tag)}</span></div>`,
        )
        .join("");
      return head + rows;
    })
    .join("");
  return `<section class="mt"><h3>My Tasks <span>${card.currentCount} <small>of ${card.total} current</small></span></h3>${tiers}
    <div class="mt-foot">Authorize a task at the equipment. Pick it on the asset's JSP page.</div></section>`;
}
